/**
 * Analytics Dashboard Page
 * 
 * Features:
 * - Date range filtering (30d, 90d, 365d, YTD, All Time)
 * - Status filtering (Active & Complete, All, etc.)
 * - Job multi-select filtering
 * - Skeleton loading states
 * - Period comparison mode
 * - PDF export for full report
 * - Customizable card/tab order and visibility (saved to localStorage)
 * 
 * Configuration-driven analytics with modular tabs.
 * See /lib/analyticsConfig.js for tab configuration.
 */

import { useState, useEffect, useMemo } from "react";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  BarChart3, TrendingUp, DollarSign, Users, RefreshCw, 
  Filter, Calendar, FileText, ChevronDown, X, GitCompare, Building2 
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { ANALYTICS_TABS, SUMMARY_CARDS, DATE_RANGE_OPTIONS, STATUS_FILTER_OPTIONS } from "../lib/analyticsConfig";
import { formatCompactCurrency, formatPercent, generateAnalyticsPdfContent } from "../lib/chartUtils";
import useExportDialog from "../hooks/useExportDialog";
import useAnalyticsPreferences from "../hooks/useAnalyticsPreferences";
import { useShowOffice } from "../hooks/useShowOffice";
import AnalyticsCustomizeDialog from "../components/analytics/AnalyticsCustomizeDialog";
import {
  ProfitabilityTab,
  LaborTab,
  MaterialsTab,
  RevenueTab,
  EmployeesTab,
  SeasonalTab,
} from "../components/analytics";
// Icon mapping for summary cards
const ICON_MAP = {
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
};

// Tab component mapping
const TAB_COMPONENT_MAP = {
  profitability: ProfitabilityTab,
  labor: LaborTab,
  materials: MaterialsTab,
  revenue: RevenueTab,
  employees: EmployeesTab,
  seasonal: SeasonalTab,
};

// Skeleton loader for summary cards
const SummaryCardSkeleton = () => (
  <Card className="stat-card">
    <div className="flex items-start justify-between">
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-8 w-24" />
      </div>
      <Skeleton className="h-5 w-5" />
    </div>
  </Card>
);

// Skeleton loader for charts
const ChartSkeleton = () => (
  <Card className="p-6">
    <Skeleton className="h-6 w-40 mb-4" />
    <Skeleton className="h-80 w-full" />
  </Card>
);

// Summary Card Component
const SummaryCard = ({ config, value, comparisonValue }) => {
  const Icon = ICON_MAP[config.icon];
  let displayValue = value;
  let color = "";
  
  if (config.format === "currency") {
    displayValue = formatCompactCurrency(value);
  } else if (config.format === "percent") {
    displayValue = formatPercent(value);
  }
  
  if (config.colorByValue) {
    color = value >= 0 ? "success" : "danger";
  }

  // Calculate comparison percentage if comparison value provided
  let comparisonPct = null;
  if (comparisonValue !== undefined && comparisonValue !== null && comparisonValue !== 0) {
    comparisonPct = ((value - comparisonValue) / Math.abs(comparisonValue) * 100).toFixed(1);
  }
  
  return (
    <Card className="stat-card" data-testid={`stat-${config.key}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="stat-label">{config.label}</p>
          <p className={`stat-value mt-1 ${color === "success" ? "text-green-600" : color === "danger" ? "text-red-600" : ""}`}>
            {displayValue}
          </p>
          {comparisonPct !== null && (
            <p className={`text-xs mt-1 ${Number(comparisonPct) >= 0 ? "text-green-600" : "text-red-600"}`}>
              {Number(comparisonPct) >= 0 ? "↑" : "↓"} {Math.abs(comparisonPct)}% vs prev
            </p>
          )}
        </div>
        {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
      </div>
    </Card>
  );
};

const AnalyticsPage = () => {
  const { getAuthHeaders } = useAuth();
  const { exportToPdf } = useExportDialog();
  const {
    preferences,
    loaded: prefsLoaded,
    reorderSummaryCards,
    toggleSummaryCard,
    reorderTabs,
    toggleTab,
    resetPreferences,
    getOrderedSummaryCards,
    getOrderedTabs,
  } = useAnalyticsPreferences();
  
  // Use shared show office preference
  const { showOffice, toggleShowOffice } = useShowOffice();

  const [data, setData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(ANALYTICS_TABS[0]?.id || "profitability");
  
  // Filter states
  const [dateRange, setDateRange] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active_complete");
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  
  // Job list for filter dropdown
  const [jobsList, setJobsList] = useState([]);

  // Get customized cards and tabs
  const visibleSummaryCards = useMemo(() => 
    getOrderedSummaryCards(SUMMARY_CARDS), 
    [getOrderedSummaryCards]
  );
  
  const visibleTabs = useMemo(() => 
    getOrderedTabs(ANALYTICS_TABS), 
    [getOrderedTabs]
  );

  // Ensure active tab is valid
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.find(t => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (dateRange !== "all") params.append("date_range", dateRange);
      if (statusFilter !== "active_complete") params.append("status_filter", statusFilter);
      if (selectedJobs.length > 0) params.append("job_ids", selectedJobs.join(","));
      if (showOffice) params.append("include_office", "true");
      
      const queryString = params.toString();
      const url = `/analytics/dashboard${queryString ? `?${queryString}` : ""}`;
      
      const response = await apiClient.get(url);
      setData(response.data);
      
      // Fetch comparison data if enabled
      if (showComparison) {
        const compParams = new URLSearchParams(params);
        compParams.set("comparison", "previous");
        const compUrl = `/analytics/dashboard?${compParams.toString()}`;
        try {
          const compResponse = await apiClient.get(compUrl);
          setComparisonData(compResponse.data);
        } catch {
          setComparisonData(null);
        }
      } else {
        setComparisonData(null);
      }
    } catch (error) {
      toast.error("Failed to load analytics data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch jobs list for filter
  const fetchJobsList = async () => {
    try {
      const response = await apiClient.get(`/jobs`);
      setJobsList(response.data.map(j => ({ id: j.id, name: j.name, status: j.status })));
    } catch (error) {
      console.error("Failed to fetch jobs list", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchJobsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when filters change
  useEffect(() => {
    if (!loading) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, statusFilter, selectedJobs, showComparison, showOffice]);

  const handleJobToggle = (jobId) => {
    setSelectedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const clearJobFilter = () => {
    setSelectedJobs([]);
  };

  const handleExportFullPDF = () => {
    if (!data) return;

    const summaryHtml = `
      <div style="display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 24px;">
        ${visibleSummaryCards.map(config => {
          const value = data.summary?.[config.key] || 0;
          let displayValue = value;
          if (config.format === "currency") displayValue = formatCompactCurrency(value);
          else if (config.format === "percent") displayValue = formatPercent(value);
          return `
            <div style="background: #f5f5f5; padding: 12px 20px; border-radius: 8px; min-width: 120px;">
              <div style="font-size: 12px; color: #666;">${config.label}</div>
              <div style="font-size: 20px; font-weight: bold; ${config.colorByValue && value >= 0 ? 'color: #16a34a;' : config.colorByValue ? 'color: #dc2626;' : ''}">${displayValue}</div>
            </div>
          `;
        }).join("")}
      </div>
    `;

    const filterInfo = `
      <div style="font-size: 12px; color: #666; margin-bottom: 16px;">
        <strong>Filters:</strong> 
        Date Range: ${DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label || "All Time"} | 
        Status: ${STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter)?.label || "Active & Complete"}
        ${selectedJobs.length > 0 ? ` | Jobs: ${selectedJobs.length} selected` : ""}
      </div>
    `;

    const content = generateAnalyticsPdfContent("Analytics Dashboard Report", [
      { title: "Applied Filters", content: filterInfo },
      { title: "Summary Metrics", content: summaryHtml },
      { title: "Note", content: "<p>For detailed reports, export individual tabs using the CSV or PDF buttons on each section.</p>" },
    ]);

    exportToPdf("Analytics Dashboard Report", content);
  };

  // Wait for preferences to load
  if (!prefsLoaded) {
    return null;
  }

  if (loading) {
    return (
      <div className="page-enter space-y-6" data-testid="analytics-page-loading">
        {/* Header Skeleton */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Skeleton className="h-10 w-40 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-24" />
        </div>

        {/* Summary Cards Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => <SummaryCardSkeleton key={i} />)}
        </div>

        {/* Tabs Skeleton */}
        <Skeleton className="h-12 w-full" />

        {/* Chart Skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No data available</p>
        <Button onClick={fetchData} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="page-enter space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">Analytics</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <AnalyticsCustomizeDialog
            summaryCards={SUMMARY_CARDS}
            tabs={ANALYTICS_TABS}
            hiddenSummaryCards={preferences.hiddenSummaryCards}
            hiddenTabs={preferences.hiddenTabs}
            onReorderSummaryCards={reorderSummaryCards}
            onToggleSummaryCard={toggleSummaryCard}
            onReorderTabs={reorderTabs}
            onToggleTab={toggleTab}
            onReset={resetPreferences}
          />
          <Button variant="outline" onClick={handleExportFullPDF} data-testid="export-full-pdf-btn">
            <FileText className="w-4 h-4 mr-2" /> Export PDF
          </Button>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-analytics-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-muted/30 rounded-lg border">
        <Filter className="w-4 h-4 text-muted-foreground" />
        
        {/* Date Range Filter */}
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-40" data-testid="date-range-filter">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent>
            {DATE_RANGE_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTER_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Job Multi-Select Filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="min-w-[140px]" data-testid="job-filter">
              Jobs {selectedJobs.length > 0 && `(${selectedJobs.length})`}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64 max-h-80 overflow-y-auto">
            {jobsList.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">No jobs available</div>
            ) : (
              jobsList.map(job => (
                <DropdownMenuCheckboxItem
                  key={job.id}
                  checked={selectedJobs.includes(job.id)}
                  onCheckedChange={() => handleJobToggle(job.id)}
                >
                  <span className="truncate">{job.name}</span>
                  <Badge variant="outline" className="ml-2 text-xs">{job.status}</Badge>
                </DropdownMenuCheckboxItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Clear Job Filter */}
        {selectedJobs.length > 0 && (
          <Button variant="ghost" size="sm" onClick={clearJobFilter}>
            <X className="w-4 h-4 mr-1" /> Clear Jobs
          </Button>
        )}

        {/* Comparison Toggle */}
        <Button
          variant={showComparison ? "default" : "outline"}
          size="sm"
          onClick={() => setShowComparison(!showComparison)}
          data-testid="comparison-toggle"
        >
          <GitCompare className="w-4 h-4 mr-2" />
          {showComparison ? "Comparing" : "Compare"}
        </Button>

        {/* Include Office Toggle */}
        <div className="flex items-center gap-2 ml-auto pl-4 border-l">
          <Switch
            id="show-office"
            checked={showOffice}
            onCheckedChange={toggleShowOffice}
            data-testid="show-office-toggle"
          />
          <Label htmlFor="show-office" className="text-sm text-muted-foreground flex items-center gap-1 cursor-pointer">
            <Building2 className="w-3.5 h-3.5" />
            Include Office
          </Label>
        </div>
      </div>

      {/* Active Filters Display */}
      {(dateRange !== "all" || statusFilter !== "active_complete" || selectedJobs.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {dateRange !== "all" && (
            <Badge variant="secondary">
              {DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label}
              <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setDateRange("all")} />
            </Badge>
          )}
          {statusFilter !== "active_complete" && (
            <Badge variant="secondary">
              {STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter)?.label}
              <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setStatusFilter("active_complete")} />
            </Badge>
          )}
          {selectedJobs.length > 0 && (
            <Badge variant="secondary">
              {selectedJobs.length} job{selectedJobs.length > 1 ? "s" : ""} selected
              <X className="w-3 h-3 ml-1 cursor-pointer" onClick={clearJobFilter} />
            </Badge>
          )}
        </div>
      )}

      {/* Summary Cards - Now customizable */}
      {visibleSummaryCards.length > 0 && (
        <div className={`grid gap-4 ${
          visibleSummaryCards.length <= 3 ? 'grid-cols-1 md:grid-cols-3' :
          visibleSummaryCards.length <= 4 ? 'grid-cols-2 md:grid-cols-4' :
          'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
        }`}>
          {visibleSummaryCards.map((config) => (
            <SummaryCard 
              key={config.key} 
              config={config} 
              value={data.summary?.[config.key]} 
              comparisonValue={showComparison ? comparisonData?.summary?.[config.key] : undefined}
            />
          ))}
        </div>
      )}

      {/* Tabs - Now customizable */}
      {visibleTabs.length > 0 && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={`grid h-auto ${
            visibleTabs.length <= 3 ? 'grid-cols-3' :
            visibleTabs.length <= 4 ? 'grid-cols-4' :
            'grid-cols-3 lg:grid-cols-6'
          }`}>
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger 
                  key={tab.id} 
                  value={tab.id} 
                  className="text-xs sm:text-sm" 
                  data-testid={`tab-${tab.id}`}
                >
                  {Icon && <Icon className="w-4 h-4 mr-1 hidden sm:inline" />}
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Tab Contents */}
          {visibleTabs.map((tab) => {
            const TabComponent = TAB_COMPONENT_MAP[tab.id];
            if (!TabComponent) return null;
            
            const tabData = data[tab.dataKey];
            
            return (
              <TabsContent key={tab.id} value={tab.id} className="space-y-6">
                <TabComponent data={tabData} />
              </TabsContent>
            );
          })}
        </Tabs>
      )}

      {/* Message if all tabs hidden */}
      {visibleTabs.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p>All tabs are hidden. Click &quot;Customize&quot; to show tabs.</p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
